/**
 * Wishlist Management System
 */
if (typeof WishlistManager === 'undefined') {
  class WishlistManager {
  constructor() {
    this.storageKey = 'rr_wishlist';
    this.metafieldKey = 'favourite-box';
    this.metafieldNamespace = 'custom';
    // Get save method, endpoints, and debug flag from drawer section settings
    const drawer = document.getElementById('wishlistDrawer');
    this.saveMethod = drawer?.getAttribute('data-save-method') || 'webhook';
    this.webhookUrl = drawer?.getAttribute('data-webhook-url') || null;

    // App endpoint – read from theme editor setting
    this.appEndpoint = drawer?.getAttribute('data-app-endpoint') || null;
    this.debug = drawer?.getAttribute('data-debug-mode') === 'true';
    
    // Guest user limits – read from theme editor settings
    const guestLimit = drawer?.getAttribute('data-guest-wishlist-limit');
    this.guestWishlistLimit = guestLimit ? parseInt(guestLimit, 10) : 5;
    this.guestLimitMessage = drawer?.getAttribute('data-guest-limit-message') || 'Please log in to save more items to your wishlist.';
    
    this.wishlist = this.getWishlist();
    this.isCustomer = this.checkIfCustomer();
    
    // Product cache - stores fetched product data to avoid redundant API calls
    this.productCache = new Map();
    this.productCacheMaxAge = 5 * 60 * 1000; // 5 minutes cache TTL
    
    // Track last saved wishlist state to avoid unnecessary saves
    this.lastSavedWishlist = JSON.stringify(this.wishlist);
    this.isSaving = false;
    this.pendingSave = false;
    
    // Debounced methods will be set up after debounce function is available
    this.init();
  }

  logDebug(...args) {
    if (!this.debug) return;
    console.log(...args);
  }

  warnDebug(...args) {
    if (!this.debug) return;
    console.warn(...args);
  }

  checkIfCustomer() {
    // Check if customer data is available from Liquid snippet
    if (window.customerWishlistData && window.customerWishlistData.isCustomer) {
      return true;
    }
    // Fallback checks
    return typeof window.Shopify?.customer !== 'undefined' || 
           document.body.classList.contains('customer-logged-in') ||
           document.querySelector('[data-customer-id]') !== null;
  }

  getCustomerId() {
    // Try to get customer ID from Liquid data (most reliable)
    if (window.customerWishlistData && window.customerWishlistData.customerId) {
      return window.customerWishlistData.customerId;
    }
    
    // Fallback: Try to get from data attribute
    const customerElement = document.querySelector('[data-customer-id]');
    if (customerElement) {
      return customerElement.getAttribute('data-customer-id');
    }
    
    // Fallback: Try Shopify customer object
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
      return window.Shopify.customer.id;
    }
    
    return null;
  }

  init() {
    this.logCustomerInfo();
    
    // Re-read drawer settings in case drawer wasn't loaded when constructor ran
    this.updateSettingsFromDrawer();
    
    document.addEventListener('wishlist:add', (e) => this.addProduct(e.detail));
    document.addEventListener('wishlist:remove', (e) => this.removeProduct(e.detail));
    document.addEventListener('wishlist:update', () => {
      this.updateDrawerDebounced();
      this.updateHeartIconsDebounced();
      // Note: saveToMetafield is handled by saveWishlist() via saveToMetafieldDebounced
      // to avoid duplicate backend calls
    });
    
    if (this.isCustomer) {
      this.syncWishlistOnLogin();
    }
    
    this.setupLoginListeners();
    this.updateDrawer();
    this.updateHeartIcons();
    
    // Setup debounced methods after initialization
    this.setupDebouncedMethods();
  }

  updateSettingsFromDrawer() {
    const drawer = document.getElementById('wishlistDrawer');
    if (drawer) {
      // Update guest limit settings if drawer is available
      const guestLimit = drawer.getAttribute('data-guest-wishlist-limit');
      if (guestLimit) {
        this.guestWishlistLimit = parseInt(guestLimit, 10);
      }
      const guestMessage = drawer.getAttribute('data-guest-limit-message');
      if (guestMessage) {
        this.guestLimitMessage = guestMessage;
      }
    }
  }

  setupDebouncedMethods() {
    // Debounce drawer updates (150ms) - prevents excessive DOM updates
    this.updateDrawerDebounced = debounce(() => {
      this.updateDrawer();
    }, 150);
    
    // Debounce heart icon updates (100ms) - prevents excessive DOM queries
    this.updateHeartIconsDebounced = debounce(() => {
      this.updateHeartIcons();
    }, 100);
  }

  logCustomerInfo() {
    if (!this.debug) return;
    const customerId = this.getCustomerId();
    this.logDebug('Customer logged in:', this.isCustomer, 'ID:', customerId);
  }

  setupLoginListeners() {
    const shopLoginButton = document.querySelector('shop-login-button');
    if (shopLoginButton) {
      shopLoginButton.addEventListener('completed', () => {
        setTimeout(() => {
          this.isCustomer = true;
          this.syncWishlistOnLogin();
        }, 1000);
      });
    }

    const accountLoginActions = document.querySelector('account-login-actions');
    if (accountLoginActions) {
      accountLoginActions.addEventListener('login-completed', () => {
        setTimeout(() => {
          this.isCustomer = true;
          this.syncWishlistOnLogin();
        }, 1000);
      });
    }

    let lastUrl = window.location.href;
    const urlCheckInterval = setInterval(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        if (this.checkIfCustomer() && !this.isCustomer) {
          this.isCustomer = true;
          this.syncWishlistOnLogin();
          clearInterval(urlCheckInterval);
        }
      }
    }, 1000);
  }

  async syncWishlistOnLogin() {
    try {
      const metafieldWishlist = await this.loadFromMetafield();
      
      if (metafieldWishlist && metafieldWishlist.length > 0) {
        const mergedWishlist = this.mergeWishlists(this.wishlist, metafieldWishlist);
        this.wishlist = mergedWishlist;
        this.saveWishlist(); // This will trigger saveToMetafieldDebounced if customer
        this.updateDrawer();
        this.updateHeartIcons();
      }
      
      // Note: saveWishlist() already handles saving via saveToMetafieldDebounced
      // No need for additional direct call to avoid duplicate backend requests
    } catch (error) {
      console.error('Error syncing wishlist on login:', error);
    }
  }

  mergeWishlists(localWishlist, metafieldWishlist) {
    const merged = [...localWishlist];
    const existingKeys = new Set(
      localWishlist.map(item => `${item.productId}-${item.variantId}`)
    );

    metafieldWishlist.forEach(item => {
      const key = `${item.productId}-${item.variantId}`;
      if (!existingKeys.has(key)) {
        merged.push(item);
        existingKeys.add(key);
      }
    });

    return merged;
  }

  async loadFromMetafield() {
    try {
      if (window.customerWishlistData && window.customerWishlistData.metafieldValue) {
        const value = window.customerWishlistData.metafieldValue;
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        
        // Handle "list of Products" metafield type
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Check if it's already in our wishlist format (has variantId)
          if (parsed[0] && parsed[0].variantId) {
            return parsed; // Already in correct format
          }
          
          // Convert list of Products to wishlist format
          return await this.convertProductsToWishlist(parsed);
        }
        
        return parsed;
      }
      
      const response = await fetch('/account/metafields.json', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const metafield = data.metafields?.find(
          m => m.namespace === this.metafieldNamespace && m.key === this.metafieldKey
        );
        
        if (metafield && metafield.value) {
          const parsed = typeof metafield.value === 'string' ? JSON.parse(metafield.value) : metafield.value;
          
          // Handle "list of Products" metafield type
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Check if it's already in our wishlist format (has variantId)
            if (parsed[0] && parsed[0].variantId) {
              return parsed; // Already in correct format
            }
            
            // Convert list of Products to wishlist format
            return await this.convertProductsToWishlist(parsed);
          }
          
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading wishlist from metafield:', error);
    }
    
    return null;
  }

  async convertProductsToWishlist(products) {
    // Convert array of Product objects to wishlist format
    const wishlistItems = [];
    
    for (const product of products) {
      try {
        // Check if product already has variant data (full product object)
        if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
          // Product already has full data, use it directly
          const variant = product.variants[0];
          const productUrl = product.url || (product.handle ? `/products/${product.handle}` : null);
          
          if (productUrl) {
            wishlistItems.push({
              productId: String(product.id),
              variantId: String(variant.id),
              handle: product.handle || '',
              title: product.title || '',
              price: variant.price || 0,
              image: variant.featured_image?.src || product.featured_image?.src || (product.images?.[0]?.src || ''),
              url: productUrl,
              addedAt: new Date().toISOString()
            });
          }
          continue;
        }
        
        // Product is a reference, need to fetch full data
        // Construct URL properly - use handle if available, otherwise try to fetch by ID
        let productUrl = product.url;
        if (!productUrl && product.handle) {
          productUrl = `/products/${product.handle}`;
        } else if (!productUrl && product.id) {
          // If we only have ID, we need to fetch product first to get handle
          // This is a fallback - ideally we should have handle
          productUrl = `/products/${product.id}`;
        }
        
        if (!productUrl) {
          console.warn('Cannot convert product: missing URL, handle, and ID', product);
          continue;
        }
        
        // Ensure URL doesn't have .js extension already
        const fetchUrl = productUrl.endsWith('.js') ? productUrl : `${productUrl}.js`;
        const response = await fetch(fetchUrl);
        
        if (response.ok) {
          const productData = await response.json();
          const variant = productData.variants?.[0] || productData.selected_or_first_available_variant;
          
          if (variant) {
            // Extract image URL properly
            let imageUrl = '';
            if (variant.featured_image) {
              imageUrl = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
            } else if (productData.featured_image) {
              imageUrl = typeof productData.featured_image === 'string' ? productData.featured_image : productData.featured_image.src;
            } else if (productData.images && productData.images.length > 0) {
              imageUrl = typeof productData.images[0] === 'string' ? productData.images[0] : productData.images[0].src;
            }
            
            wishlistItems.push({
              productId: String(productData.id || product.id),
              variantId: String(variant.id),
              handle: productData.handle || product.handle || '',
              title: productData.title || product.title || '',
              price: variant.price || 0,
              image: imageUrl,
              url: productData.url || productUrl,
              addedAt: new Date().toISOString()
            });
          }
        } else {
          // Fallback: create item with minimal data if we have handle
          if (product.handle || product.id) {
            const fallbackUrl = product.handle ? `/products/${product.handle}` : `/products/${product.id}`;
            wishlistItems.push({
              productId: String(product.id),
              variantId: String(product.id), // Use product ID as fallback
              handle: product.handle || '',
              title: product.title || '',
              price: 0,
              image: '',
              url: fallbackUrl,
              addedAt: new Date().toISOString()
            });
          }
        }
      } catch (error) {
        console.error('Error converting product to wishlist item:', error, product);
        // Skip this product and continue
      }
    }
    
    return wishlistItems;
  }

  async saveToMetafield() {
    if (!this.isCustomer) {
      this.logDebug('Cannot save: Customer not logged in');
      return;
    }

    // Prevent duplicate saves
    if (this.isSaving) {
      this.logDebug('Save already in progress, skipping...');
      this.pendingSave = true;
      return;
    }

    // Check if wishlist actually changed
    const currentWishlistString = JSON.stringify(this.wishlist);
    if (currentWishlistString === this.lastSavedWishlist) {
      this.logDebug('Wishlist unchanged, skipping save');
      this.pendingSave = false;
      return;
    }

    try {
      this.isSaving = true;
      this.pendingSave = false;
      
      // Get customer ID
      const customerId = this.getCustomerId();
      
      if (!customerId) {
        this.warnDebug('Customer ID not available');
        throw new Error('Customer ID not available. Cannot save wishlist.');
      }
      
      this.logDebug('Saving wishlist:', this.wishlist.length, 'items, method:', this.saveMethod);
      
      // Save via selected method
      let result;
      if (this.saveMethod === 'app') {
        result = await this.saveViaAppEndpoint(customerId);
      } else {
        result = await this.saveViaWebhook(customerId);
      }
      
      // Update last saved state only on success
      this.lastSavedWishlist = currentWishlistString;
      
      // If there was a pending save while we were saving, trigger another save
      if (this.pendingSave) {
        this.logDebug('Pending save detected, will save again after delay');
        setTimeout(() => {
          if (this.pendingSave) {
            this.saveToMetafieldDebounced();
          }
        }, 500);
      }
      
      return result;
    } catch (error) {
      console.error('Error saving wishlist to metafield:', error);
      // Don't update lastSavedWishlist on error so we can retry
      throw error; // Re-throw so sync button can show error state
    } finally {
      this.isSaving = false;
    }
  }

  async saveViaAppEndpoint(customerId, retryCount = 0) {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second
    
    try {
      if (!this.appEndpoint) {
        throw new Error('App endpoint not configured');
      }


      const url = this.appEndpoint;

      // For "list of Products" metafield, extract unique product IDs
      // The app should use productIds for the metafield value
      const productIds = [...new Set(this.wishlist.map(item => String(item.productId)))];
      
      const payload = {
        customerId,
        customerEmail: window.customerWishlistData?.customerEmail || null,
        namespace: this.metafieldNamespace,
        key: this.metafieldKey,
        metafieldType: 'list.product_reference', // Indicate this is a list of Products metafield
        // For the metafield value, send product IDs (for list of Products metafield)
        productIds: productIds,
        // Also send full wishlist data for app's internal use (variant info, etc.)
        wishlist: this.wishlist,
        wishlistJson: JSON.stringify(this.wishlist), // Full data as JSON for app reference
        wishlistCount: this.wishlist.length,
        timestamp: new Date().toISOString(),
        shop: window.Shopify?.shop || window.location.hostname,
        retryAttempt: retryCount,
        source: 'theme_app_endpoint'
      };

      this.logDebug('Sending to app endpoint:', url, 'Attempt:', retryCount + 1);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        let data;
        try {
          data = await response.json();
        } catch (e) {
          data = {
            status: 'success',
            statusCode: response.status,
            message: 'App endpoint received successfully'
          };
        }

        this.logDebug('App endpoint success:', response.status);

        return data;
      } else {
        let errorText;
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }

        console.error('App endpoint error response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });

        if (retryCount < maxRetries && (response.status >= 500 || response.status === 0)) {
          this.logDebug('Retrying app endpoint...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaAppEndpoint(customerId, retryCount + 1);
        }

        throw new Error(`App endpoint error: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('App endpoint request timed out');
        if (retryCount < maxRetries) {
          this.logDebug('Retrying app endpoint...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaAppEndpoint(customerId, retryCount + 1);
        }
        throw new Error('App endpoint request timed out. Please check your app endpoint and server availability.');
      }

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error (app endpoint):', error);
        if (retryCount < maxRetries) {
          this.logDebug('Retrying app endpoint...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaAppEndpoint(customerId, retryCount + 1);
        }
        throw new Error('Network error: Could not connect to app endpoint. Please check your internet connection and app URL.');
      }

      console.error('Error saving via app endpoint:', error);
      throw error;
    }
  }

  async saveViaWebhook(customerId, retryCount = 0) {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second
    
    try {
      if (!this.webhookUrl) {
        throw new Error('Webhook URL not configured');
      }

      // Validate webhook URL format
      try {
        new URL(this.webhookUrl);
      } catch (e) {
        throw new Error('Invalid webhook URL format');
      }

      // For "list of Products" metafield, extract unique product IDs
      // The webhook/app should use productIds for the metafield value
      const productIds = [...new Set(this.wishlist.map(item => String(item.productId)))];
      
      // Prepare comprehensive data for webhook
      const webhookData = {
        customerId: customerId,
        customerEmail: window.customerWishlistData?.customerEmail || null,
        namespace: this.metafieldNamespace,
        key: this.metafieldKey,
        metafieldType: 'list.product_reference', // Indicate this is a list of Products metafield
        // For the metafield value, send product IDs (for list of Products metafield)
        productIds: productIds,
        // Also send full wishlist data for webhook's internal use (variant info, etc.)
        wishlist: this.wishlist,
        wishlistJson: JSON.stringify(this.wishlist), // Full data as JSON for webhook reference
        wishlistCount: this.wishlist.length,
        timestamp: new Date().toISOString(),
        shop: window.Shopify?.shop || window.location.hostname,
        retryAttempt: retryCount
      };
      
      this.logDebug('Sending to webhook:', this.webhookUrl, 'Attempt:', retryCount + 1);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(webhookData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Check for successful response (200-299 range)
      if (response.ok) {
        let responseData;
        try {
          responseData = await response.json();
        } catch (e) {
          // If response is not JSON, still consider it successful if status is 200-299
          responseData = { 
            status: 'success', 
            statusCode: response.status,
            message: 'Webhook received successfully'
          };
        }
        
        this.logDebug('Webhook success:', response.status);
        
        return responseData;
      } else {
        // Handle different error status codes
        let errorText;
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        console.error('Webhook error response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });
        
        // Retry on server errors (5xx) or network-like errors
        if (retryCount < maxRetries && (response.status >= 500 || response.status === 0)) {
          this.logDebug('Retrying webhook...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaWebhook(customerId, retryCount + 1);
        }
        
        throw new Error(`Webhook error: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      // Handle network errors and timeouts
      if (error.name === 'AbortError') {
        console.error('Webhook request timed out');
        if (retryCount < maxRetries) {
          this.logDebug('Retrying webhook...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaWebhook(customerId, retryCount + 1);
        }
        throw new Error('Webhook request timed out. Please check your webhook URL and server availability.');
      }
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error:', error);
        if (retryCount < maxRetries) {
          this.logDebug('Retrying webhook...');
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return await this.saveViaWebhook(customerId, retryCount + 1);
        }
        throw new Error('Network error: Could not connect to webhook. Please check your internet connection and webhook URL.');
      }
      
      console.error('Error saving via webhook:', error);
      throw error;
    }
  }

  getWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      const wishlist = stored ? JSON.parse(stored) : [];
      
      // Limit wishlist to configured limit for non-logged-in users
      if (!this.isCustomer && wishlist.length > this.guestWishlistLimit) {
        this.logDebug(`Trimming wishlist to ${this.guestWishlistLimit} items for non-logged-in user`);
        // Keep the most recently added items
        const sorted = wishlist.sort((a, b) => {
          const dateA = a.addedAt ? new Date(a.addedAt).getTime() : 0;
          const dateB = b.addedAt ? new Date(b.addedAt).getTime() : 0;
          return dateB - dateA; // Most recent first
        });
        const trimmed = sorted.slice(0, this.guestWishlistLimit);
        // Save the trimmed wishlist back to localStorage
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(trimmed));
        } catch (e) {
          console.error('Error saving trimmed wishlist:', e);
        }
        return trimmed;
      }
      
      return wishlist;
    } catch (e) {
      console.error('Error reading wishlist from localStorage:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      document.dispatchEvent(new CustomEvent('wishlist:update'));
      
      // Check if wishlist actually changed before triggering save
      const currentWishlistString = JSON.stringify(this.wishlist);
      const hasChanged = currentWishlistString !== this.lastSavedWishlist;
      
      // Auto-save to metafield if customer is logged in and wishlist changed (debounced)
      if (this.isCustomer && hasChanged && this.saveToMetafieldDebounced) {
        this.pendingSave = true;
        this.saveToMetafieldDebounced();
      }
    } catch (e) {
      console.error('Error saving wishlist to localStorage:', e);
    }
  }

    async addProduct(productData) {
      const { productId, variantId, handle, title, price, image, url, variantsCount, hasMultipleVariants } = productData;
      
      // Check if product already exists
      const existingIndex = this.wishlist.findIndex(
        item => String(item.productId) === String(productId) && String(item.variantId) === String(variantId)
      );

      if (existingIndex === -1) {
        // Limit wishlist to configured limit for non-logged-in users
        if (!this.isCustomer && this.wishlist.length >= this.guestWishlistLimit) {
          this.logDebug(`Wishlist limit reached (${this.guestWishlistLimit} items) for non-logged-in user`);
          // Show a message to the user
          this.showLimitMessage();
          return false;
        }
        
        // Ensure we have a valid URL or handle - if missing, try to fetch product data
        let productUrl = url;
        let productHandle = handle;
        
        if (!productUrl && !productHandle && productId) {
          // Try to fetch product data using product ID
          try {
            const fetchUrl = `/products/${productId}.js`;
            const response = await fetch(fetchUrl);
            if (response.ok) {
              const product = await response.json();
              productUrl = product.url || (product.handle ? `/products/${product.handle}` : null);
              productHandle = product.handle || handle;
              
              // Update the productData with fetched information
              if (productUrl && !url) productData.url = productUrl;
              if (productHandle && !handle) productData.handle = productHandle;
            }
          } catch (error) {
            // Silently fail - will use existing data
          }
        }
        
        // Clean URL - remove query parameters before storing
        let cleanUrl = productUrl || url;
        if (cleanUrl) {
          try {
            const urlObj = new URL(cleanUrl, window.location.origin);
            cleanUrl = urlObj.pathname; // Get just the path without query params
          } catch (e) {
            // If URL parsing fails, try to manually remove query params
            cleanUrl = cleanUrl.split('?')[0].split('#')[0];
          }
        }
        
        const wishlistItem = {
          productId,
          variantId,
          handle: productHandle || handle,
          title,
          price,
          image,
          url: cleanUrl, // Store cleaned URL
          addedAt: new Date().toISOString()
        };
        
        // Store variant info if provided (from Liquid data attributes)
        if (variantsCount !== undefined && variantsCount !== null) {
          wishlistItem.variantsCount = variantsCount;
        }
        if (hasMultipleVariants !== undefined && hasMultipleVariants !== null) {
          wishlistItem.hasMultipleVariants = hasMultipleVariants;
        }
        
        this.wishlist.push(wishlistItem);
        this.saveWishlist();
        this.updateHeartIconsDebounced();
        return true;
      }
      return false;
    }

    removeProduct({ productId, variantId }) {
      const index = this.wishlist.findIndex(
        item => String(item.productId) === String(productId) && String(item.variantId) === String(variantId)
      );

      if (index !== -1) {
        const removedItem = this.wishlist[index];
        this.wishlist.splice(index, 1);
        
        // Clear cache for removed product if no other variants are in wishlist
        const hasOtherVariants = this.wishlist.some(
          item => String(item.productId) === String(productId)
        );
        if (!hasOtherVariants && removedItem.url) {
          this.productCache.delete(removedItem.url);
        }
        
        this.saveWishlist();
        
        // Update heart icons immediately for instant feedback
        this.updateHeartIcons();
        
        // Also trigger debounced update to catch any missed buttons
        if (this.updateHeartIconsDebounced) {
          this.updateHeartIconsDebounced();
        }
        
        return true;
      }
      return false;
    }

  isInWishlist(productId, variantId) {
    return this.wishlist.some(
      item => String(item.productId) === String(productId) && String(item.variantId) === String(variantId)
    );
  }

  getCount() {
    return this.wishlist.length;
  }

  async updateDrawer() {
    const drawerContent = document.getElementById('wishlistDrawerContent');
    if (!drawerContent) return;

    if (this.wishlist.length === 0) {
      drawerContent.innerHTML = `
        <div class="wishlist-empty" style="padding: 40px 20px; text-align: center;">
          <i class="ph ph-heart" style="font-size: 48px; color: #ccc; margin-bottom: 16px; display: block;"></i>
          <p style="color: #666; font-size: 16px; margin-bottom: 8px;">Your wishlist is empty</p>
          <p style="color: #999; font-size: 14px;">Start adding products to your wishlist!</p>
        </div>
      `;
      return;
    }

    // Clean expired cache entries
    this.cleanProductCache();

    const productsHTML = await Promise.all(
      this.wishlist.map(async (item) => {
        try {
          // Build product URL - prioritize stored URL, then handle, then try to fetch by product ID
          let productUrl = item.url;
          
          // Clean URL - remove query parameters and hash (e.g., ?pr_prod_s...)
          if (productUrl) {
            try {
              const urlObj = new URL(productUrl, window.location.origin);
              productUrl = urlObj.pathname; // Get just the path without query params
            } catch (e) {
              // If URL parsing fails, try to manually remove query params
              productUrl = productUrl.split('?')[0].split('#')[0];
            }
          }
          
          if (!productUrl && item.handle) {
            productUrl = `/products/${item.handle}`;
          }
          
          // If we still don't have a URL but have productId, try to fetch product by ID
          // This is a fallback for products added from dynamic sections
          if (!productUrl && item.productId) {
            // Try to fetch product data using Shopify's product API
            try {
              const productResponse = await fetch(`/products/${item.productId}.js`);
              if (productResponse.ok) {
                const product = await productResponse.json();
                productUrl = product.url || (product.handle ? `/products/${product.handle}` : null);
                // Update the item with the correct URL for future use
                if (productUrl) {
                  item.url = productUrl;
                  if (!item.handle && product.handle) {
                    item.handle = product.handle;
                  }
                  // Save updated wishlist
                  this.saveWishlist();
                }
              }
            } catch (fetchError) {
              // Silently fail - will use fallback rendering
            }
          }
          
          if (!productUrl) {
            return this.renderWishlistItemFallback(item);
          }
          
          // Check cache first
          const cached = this.productCache.get(productUrl);
          if (cached && cached.product && cached.timestamp) {
            const age = Date.now() - cached.timestamp;
            if (age < this.productCacheMaxAge) {
              // Verify cached product has variants
              if (cached.product.variants && Array.isArray(cached.product.variants) && cached.product.variants.length > 0) {
                return this.renderWishlistItem(cached.product, item.variantId);
              } else {
                // Cached product missing variants, refetch
                this.productCache.delete(productUrl);
              }
            } else {
              // Cache expired, remove it
              this.productCache.delete(productUrl);
            }
          }
          
          // Validate and clean the product URL before fetching
          // Remove any query parameters, hash, and ensure it's a valid product path
          let cleanProductUrl = productUrl;
          if (cleanProductUrl) {
            // Remove query params and hash
            cleanProductUrl = cleanProductUrl.split('?')[0].split('#')[0];
            // Ensure it starts with /products/
            if (!cleanProductUrl.startsWith('/products/')) {
              // If it doesn't start with /products/, try to construct it from handle
              if (item.handle) {
                cleanProductUrl = `/products/${item.handle}`;
              } else {
                return this.renderWishlistItemFallback(item);
              }
            }
            // Remove any trailing slashes or invalid characters
            cleanProductUrl = cleanProductUrl.replace(/\/+$/, '').trim();
          }
          
          // Ensure URL doesn't already have .js extension and is clean
          let fetchUrl = cleanProductUrl.endsWith('.js') ? cleanProductUrl : `${cleanProductUrl}.js`;
          
          // Final validation - ensure fetchUrl is a valid path
          if (!fetchUrl.startsWith('/products/') || !fetchUrl.endsWith('.js')) {
            return this.renderWishlistItemFallback(item);
          }
          
          // Fetch product data - always fetch fresh to ensure we have all variants
          let response;
          try {
            response = await fetch(fetchUrl);
          } catch (fetchError) {
            return this.renderWishlistItemFallback(item);
          }
          
          if (response.ok) {
            let product;
            try {
              product = await response.json();
            } catch (jsonError) {
              return this.renderWishlistItemFallback(item);
            }
            
            // Ensure product has variants array
            if (!product.variants || !Array.isArray(product.variants)) {
              if (product.variant) {
                product.variants = [product.variant];
              } else {
                return this.renderWishlistItemFallback(item);
              }
            }
            
            // Ensure variants array is not empty
            if (product.variants.length === 0) {
              return this.renderWishlistItemFallback(item);
            }
            
            if (this.debug) {
              this.logDebug('Product fetched for wishlist:', {
                id: product.id,
                title: product.title,
                url: productUrl,
                variantsCount: product.variants?.length || 0,
                hasMultipleVariants: product.variants?.length > 1
              });
            }
            
            // Store in cache with timestamp (use cleaned URL as key)
            this.productCache.set(cleanProductUrl, {
              product: product,
              timestamp: Date.now()
            });
            return this.renderWishlistItem(product, item.variantId);
          } else {
            return this.renderWishlistItemFallback(item);
          }
        } catch (e) {
          return this.renderWishlistItemFallback(item);
        }
      })
    );

    drawerContent.innerHTML = `
      <div class="wishlist-items" style="padding: 20px;">
        ${productsHTML.join('')}
      </div>
    `;

    this.setupRemoveButtons();
    this.setupAddToCartButtons();
    this.setupVariantSelects();
  }

  cleanProductCache() {
    const now = Date.now();
    for (const [url, cached] of this.productCache.entries()) {
      if (!cached.timestamp || (now - cached.timestamp) > this.productCacheMaxAge) {
        this.productCache.delete(url);
      }
    }
  }

  clearProductCache() {
    this.productCache.clear();
  }

  renderWishlistItem(product, variantId) {
    // Ensure we have variants array
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      return this.renderWishlistItemFallback({ productId: product.id, variantId, title: product.title, price: 0, url: product.url });
    }
    
    const variant = product.variants.find(v => String(v.id) === String(variantId)) || product.variants[0];
    if (!variant) {
      return this.renderWishlistItemFallback({ productId: product.id, variantId, title: product.title, price: product.variants[0]?.price || 0, url: product.url });
    }
    
    // Extract image URL - Shopify product JSON has image as object with src property
    let imageUrl = '';
    if (variant.featured_image) {
      imageUrl = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
    } else if (product.featured_image) {
      imageUrl = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src;
    } else if (product.images && product.images.length > 0) {
      imageUrl = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
    }
    
    const price = variant.price;
    const comparePrice = variant.compare_at_price;
    
    // Build variant options dropdown (only show if more than one variant)
    const hasMultipleVariants = Array.isArray(product.variants) && product.variants.length > 1;
    
    const variantOptions = hasMultipleVariants ? product.variants.map(v => {
      const optionText = v.title !== 'Default Title' ? v.title : `${product.title} - ${v.title}`;
      const isSelected = String(v.id) === String(variantId);
      return `<option value="${v.id}" ${isSelected ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${optionText}${!v.available ? ' (Sold Out)' : ''}</option>`;
    }).join('') : '';

    return `
      <div class="wishlist-item" data-product-id="${product.id}" data-variant-id="${variantId}" style="
        display: flex;
        gap: 16px;
        padding: 16px;
        border-bottom: 1px solid #e5e5e5;
        position: relative;
      ">
        <a href="${product.url}" style="flex-shrink: 0; width: 80px; height: 80px; overflow: hidden; border-radius: 8px;">
          ${imageUrl ? `
            <img src="${imageUrl}" alt="${product.title}" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy">
          ` : `
            <div style="width: 100%; height: 100%; background: #f5f5f5; display: flex; align-items: center; justify-content: center;">
              <i class="ph ph-image" style="font-size: 24px; color: #ccc;"></i>
            </div>
          `}
        </a>
        <div style="flex: 1; min-width: 0;">
          <a href="${product.url}" style="text-decoration: none; color: inherit;">
            <h4 style="font-size: 14px; font-weight: 500; margin: 0 0 8px 0; line-height: 1.4; color: #000;">
              ${product.title}
            </h4>
          </a>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <span style="font-size: 16px; font-weight: 600; color: #000;">
              ${this.formatMoney(price)}
            </span>
            ${comparePrice && comparePrice > price ? `
              <span style="font-size: 14px; color: #999; text-decoration: line-through;">
                ${this.formatMoney(comparePrice)}
              </span>
            ` : ''}
          </div>
          ${hasMultipleVariants ? `
            <select class="wishlist-variant-select" data-product-id="${product.id}" data-original-variant-id="${variantId}" style="
              width: 100%;
              padding: 8px 12px;
              margin-bottom: 12px;
              border: 1px solid #e5e5e5;
              border-radius: 4px;
              font-size: 14px;
              background: #fff;
              color: #000;
              cursor: pointer;
              appearance: none;
              background-image: url('data:image/svg+xml;utf8,<svg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'12\\' height=\\'12\\' viewBox=\\'0 0 12 12\\'><path fill=\\'%23333\\' d=\\'M6 9L1 4h10z\\'/></svg>');
              background-repeat: no-repeat;
              background-position: right 12px center;
              padding-right: 32px;
            ">
              ${variantOptions}
            </select>
          ` : ''}
          <button class="wishlist-add-to-cart-btn" data-variant-id="${variant.id}" data-product-id="${product.id}" style="
            background: ${variant.available ? '#000' : '#ccc'};
            color: #fff;
            border: none;
            padding: 10px 16px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: ${variant.available ? 'pointer' : 'not-allowed'};
            transition: all 0.2s;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          " onmouseover="${variant.available ? "this.style.background='#333';" : ''}" onmouseout="${variant.available ? "this.style.background='#000';" : ''}" ${!variant.available ? 'disabled' : ''}>
            <span class="wishlist-cart-btn-text">${variant.available ? 'Add to Cart' : 'Sold Out'}</span>
            <i class="ph ph-circle-notch wishlist-cart-btn-spinner" style="display: none; animation: spin 1s linear infinite;"></i>
          </button>
        </div>
        <button class="wishlist-item-remove" data-product-id="${product.id}" data-variant-id="${variantId}" style="
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        " onmouseover="this.style.background='#fff'; this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)';" onmouseout="this.style.background='rgba(255,255,255,0.9)'; this.style.transform='scale(1)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)';" aria-label="Remove from wishlist">
          <i class="ph ph-trash" style="font-size: 16px; color: #666;"></i>
        </button>
      </div>
    `;
  }

  renderWishlistItemFallback(item) {
    const productUrl = item.url || (item.handle ? `/products/${item.handle}` : '#');
    const imageUrl = item.image || '';
    const isAvailable = item.price !== undefined && item.price !== null;
    
    return `
      <div class="wishlist-item" data-product-id="${item.productId}" data-variant-id="${item.variantId}" style="
        display: flex;
        gap: 16px;
        padding: 16px;
        border-bottom: 1px solid #e5e5e5;
        position: relative;
      ">
        <a href="${productUrl}" style="flex-shrink: 0; width: 80px; height: 80px; overflow: hidden; border-radius: 8px;">
          ${imageUrl ? `
            <img src="${imageUrl}" alt="${item.title || 'Product'}" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy">
          ` : `
            <div style="width: 100%; height: 100%; background: #f5f5f5; display: flex; align-items: center; justify-content: center;">
              <i class="ph ph-image" style="font-size: 24px; color: #ccc;"></i>
            </div>
          `}
        </a>
        <div style="flex: 1; min-width: 0;">
          <a href="${productUrl}" style="text-decoration: none; color: inherit;">
            <h4 style="font-size: 14px; font-weight: 500; margin: 0 0 8px 0; line-height: 1.4; color: #000;">
              ${item.title || 'Product'}
            </h4>
          </a>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <span style="font-size: 16px; font-weight: 600; color: #000;">
              ${this.formatMoney(item.price || 0)}
            </span>
          </div>
          <button class="wishlist-add-to-cart-btn" data-variant-id="${item.variantId}" data-product-id="${item.productId}" style="
            background: ${isAvailable ? '#000' : '#ccc'};
            color: #fff;
            border: none;
            padding: 10px 16px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: ${isAvailable ? 'pointer' : 'not-allowed'};
            transition: all 0.2s;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          " onmouseover="${isAvailable ? "this.style.background='#333';" : ''}" onmouseout="${isAvailable ? "this.style.background='#000';" : ''}" ${!isAvailable ? 'disabled' : ''}>
            <span class="wishlist-cart-btn-text">${isAvailable ? 'Add to Cart' : 'Sold Out'}</span>
            <i class="ph ph-circle-notch wishlist-cart-btn-spinner" style="display: none; animation: spin 1s linear infinite;"></i>
          </button>
        </div>
        <button class="wishlist-item-remove" data-product-id="${item.productId}" data-variant-id="${item.variantId}" style="
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        " onmouseover="this.style.background='#fff'; this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)';" onmouseout="this.style.background='rgba(255,255,255,0.9)'; this.style.transform='scale(1)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)';" aria-label="Remove from wishlist">
          <i class="ph ph-trash" style="font-size: 16px; color: #666;"></i>
        </button>
      </div>
    `;
  }

  setupRemoveButtons() {
    const removeButtons = document.querySelectorAll('.wishlist-item-remove');
    removeButtons.forEach(btn => {
      // Remove existing listeners to prevent duplicates
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productId = newBtn.dataset.productId;
        const variantId = newBtn.dataset.variantId;
        
        // Remove product - this will trigger saveWishlist() which dispatches wishlist:update
        this.removeProduct({ productId, variantId });
        
        // Update heart icons immediately (not debounced) for instant feedback
        this.updateHeartIcons();
        
        // Also ensure the wishlist:update event triggers heart icon updates
        // The removeProduct already calls updateHeartIconsDebounced via saveWishlist,
        // but we call it directly here too to ensure it happens
        setTimeout(() => {
          this.updateHeartIcons();
        }, 50);
      });
    });
  }

  setupAddToCartButtons() {
    const addToCartButtons = document.querySelectorAll('.wishlist-add-to-cart-btn');
    addToCartButtons.forEach(btn => {
      // Remove existing listeners to prevent duplicates
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (newBtn.disabled) return;
        
        const variantId = newBtn.dataset.variantId;
        const buttonText = newBtn.querySelector('.wishlist-cart-btn-text');
        const spinner = newBtn.querySelector('.wishlist-cart-btn-spinner');
        
        if (!variantId) {
          console.error('Missing variant ID');
          return;
        }
        
        // Show loading state
        newBtn.disabled = true;
        if (buttonText) buttonText.textContent = 'Adding...';
        if (spinner) spinner.style.display = 'inline-block';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });
          
          if (response.ok) {
            // Show success state
            if (buttonText) buttonText.textContent = 'Added!';
            if (spinner) spinner.style.display = 'none';
            newBtn.style.background = '#22c55e';
            
            // Fetch updated cart to get accurate item count
            try {
              const cartResponse = await fetch('/cart.js');
              const cart = await cartResponse.json();
              
              // Calculate total item count
              const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
              
              // Trigger cart update event with proper data structure (matching other rr files)
              const cartUpdateEvent = new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'rr-wishlist',
                  data: {
                    source: 'rr-wishlist',
                    itemCount: itemCount
                  }
                }
              });
              document.dispatchEvent(cartUpdateEvent);
              
              // Also dispatch the simpler event for backwards compatibility
              document.dispatchEvent(new CustomEvent('cart:updated', {
                bubbles: true,
                detail: { itemCount: itemCount }
              }));
              
              // Also dispatch cart:refresh for components that listen to it
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            } catch (cartError) {
              console.error('Error fetching cart:', cartError);
              // Still dispatch events even if cart fetch fails
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            
            // Reset button after 2 seconds
            setTimeout(() => {
              if (buttonText) buttonText.textContent = 'Add to Cart';
              newBtn.style.background = '#000';
              newBtn.disabled = false;
            }, 2000);
          } else {
            const error = await response.json();
            throw new Error(error.message || 'Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          if (buttonText) buttonText.textContent = 'Error';
          if (spinner) spinner.style.display = 'none';
          newBtn.style.background = '#ef4444';
          
          setTimeout(() => {
            if (buttonText) buttonText.textContent = 'Add to Cart';
            newBtn.style.background = '#000';
            newBtn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  setupVariantSelects() {
    const variantSelects = document.querySelectorAll('.wishlist-variant-select');
    variantSelects.forEach(select => {
      select.addEventListener('change', async (e) => {
        const newVariantId = e.target.value;
        const productId = select.dataset.productId;
        const originalVariantId = select.dataset.originalVariantId;
        const wishlistItem = select.closest('.wishlist-item');
        
        if (!wishlistItem || !newVariantId) return;
        
        // Find the product data from cache or fetch it
        const productUrl = wishlistItem.querySelector('a[href*="/products/"]')?.href;
        if (!productUrl) return;
        
        let product = null;
        const cached = this.productCache.get(productUrl);
        if (cached && cached.product) {
          product = cached.product;
        } else {
          try {
            const fetchUrl = productUrl.endsWith('.js') ? productUrl : `${productUrl}.js`;
            const response = await fetch(fetchUrl);
            if (response.ok) {
              product = await response.json();
              this.productCache.set(productUrl, {
                product: product,
                timestamp: Date.now()
              });
            }
          } catch (error) {
            console.error('Error fetching product for variant change:', error);
            return;
          }
        }
        
        if (!product) return;
        
        // Find the new variant
        const newVariant = product.variants.find(v => v.id == newVariantId);
        if (!newVariant) return;
        
        // Update the wishlist item's variant ID
        wishlistItem.setAttribute('data-variant-id', newVariantId);
        
        // Update price display
        const priceContainer = wishlistItem.querySelector('div[style*="display: flex"][style*="align-items: center"]');
        if (priceContainer) {
          const priceSpan = priceContainer.querySelector('span[style*="font-size: 16px"]');
          const comparePriceSpan = priceContainer.querySelector('span[style*="text-decoration: line-through"]');
          
          if (priceSpan) {
            priceSpan.textContent = this.formatMoney(newVariant.price);
          }
          
          if (comparePriceSpan) {
            if (newVariant.compare_at_price && newVariant.compare_at_price > newVariant.price) {
              comparePriceSpan.textContent = this.formatMoney(newVariant.compare_at_price);
              comparePriceSpan.style.display = '';
            } else {
              comparePriceSpan.style.display = 'none';
            }
          } else if (newVariant.compare_at_price && newVariant.compare_at_price > newVariant.price) {
            // Add compare price if it doesn't exist
            const comparePrice = document.createElement('span');
            comparePrice.style.cssText = 'font-size: 14px; color: #999; text-decoration: line-through;';
            comparePrice.textContent = this.formatMoney(newVariant.compare_at_price);
            priceContainer.appendChild(comparePrice);
          }
        }
        
        // Update image if variant has a different image
        if (newVariant.featured_image) {
          const imageUrl = typeof newVariant.featured_image === 'string' 
            ? newVariant.featured_image 
            : newVariant.featured_image.src;
          const img = wishlistItem.querySelector('img[alt]');
          if (img && imageUrl) {
            img.src = imageUrl;
          }
        }
        
        // Update add to cart button
        const addToCartBtn = wishlistItem.querySelector('.wishlist-add-to-cart-btn');
        if (addToCartBtn) {
          addToCartBtn.dataset.variantId = newVariantId;
          const btnText = addToCartBtn.querySelector('.wishlist-cart-btn-text');
          if (btnText) {
            btnText.textContent = newVariant.available ? 'Add to Cart' : 'Sold Out';
          }
          
          if (newVariant.available) {
            addToCartBtn.disabled = false;
            addToCartBtn.style.background = '#000';
            addToCartBtn.style.cursor = 'pointer';
            addToCartBtn.onmouseover = () => { addToCartBtn.style.background = '#333'; };
            addToCartBtn.onmouseout = () => { addToCartBtn.style.background = '#000'; };
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.style.background = '#ccc';
            addToCartBtn.style.cursor = 'not-allowed';
            addToCartBtn.onmouseover = null;
            addToCartBtn.onmouseout = null;
          }
        }
        
        // Update remove button variant ID
        const removeBtn = wishlistItem.querySelector('.wishlist-item-remove');
        if (removeBtn) {
          removeBtn.dataset.variantId = newVariantId;
        }
        
        // Update wishlist in memory and localStorage
        const wishlistItemIndex = this.wishlist.findIndex(
          item => String(item.productId) === String(productId) && String(item.variantId) === String(originalVariantId)
        );
        
        if (wishlistItemIndex !== -1) {
          // Check if new variant already exists in wishlist
          const existingNewVariant = this.wishlist.findIndex(
            item => String(item.productId) === String(productId) && String(item.variantId) === String(newVariantId)
          );
          
          if (existingNewVariant === -1) {
            // Update the variant ID
            this.wishlist[wishlistItemIndex].variantId = String(newVariantId);
            this.saveWishlist();
          } else {
            // New variant already in wishlist, remove the old one
            this.wishlist.splice(wishlistItemIndex, 1);
            this.saveWishlist();
            // Remove the item from DOM
            wishlistItem.remove();
            // Update drawer to refresh
            this.updateDrawer();
            return;
          }
        }
      });
    });
  }

  updateHeartIcons() {
    const hearts = document.querySelectorAll('[data-wishlist-heart]');
    hearts.forEach(heart => {
      const productId = heart.dataset.productId;
      const variantId = heart.dataset.variantId;
      if (!productId || !variantId) return;
      
      const isInWishlist = this.isInWishlist(productId, variantId);
      const defaultIcons = heart.querySelectorAll('.wishlist-icon-default');
      const activeIcons = heart.querySelectorAll('.wishlist-icon-active');
      
      if (isInWishlist) {
        heart.classList.add('in-wishlist');
        defaultIcons.forEach(icon => {
          if (icon.tagName === 'IMG' || icon.classList.contains('ph-icon-fallback')) {
            icon.style.display = 'none';
          }
        });
        activeIcons.forEach(icon => {
          if (icon.tagName === 'IMG' || icon.classList.contains('ph-icon-fallback')) {
            icon.style.display = 'block';
          }
        });
      } else {
        heart.classList.remove('in-wishlist');
        defaultIcons.forEach(icon => {
          if (icon.tagName === 'IMG' || icon.classList.contains('ph-icon-fallback')) {
            icon.style.display = 'block';
          }
        });
        activeIcons.forEach(icon => {
          if (icon.tagName === 'IMG' || icon.classList.contains('ph-icon-fallback')) {
            icon.style.display = 'none';
          }
        });
      }
    });
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  showLimitMessage() {
    // Use the customizable message from theme editor
    const message = this.guestLimitMessage || 'Please log in to save more items to your wishlist.';
    
    // Try to find an existing notification element
    let notification = document.getElementById('wishlist-limit-notification');
    
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'wishlist-limit-notification';
      notification.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #000;
        color: #fff;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 10000;
        font-size: 14px;
        max-width: 300px;
        line-height: 1.5;
        animation: slideIn 0.3s ease;
      `;
      
      // Add animation style if not already added
      if (!document.getElementById('wishlist-limit-notification-style')) {
        const style = document.createElement('style');
        style.id = 'wishlist-limit-notification-style';
        style.textContent = `
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          #wishlist-limit-notification {
            animation: slideIn 0.3s ease;
          }
        `;
        document.head.appendChild(style);
      }
      
      document.body.appendChild(notification);
    }
    
    notification.textContent = message;
    notification.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (notification) {
        notification.style.display = 'none';
      }
    }, 5000);
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

if (!window.wishlistManager) {
  const wishlistManager = new WishlistManager();
  // Increased debounce time to 3 seconds to batch rapid add/remove operations
  wishlistManager.saveToMetafieldDebounced = debounce(() => {
    wishlistManager.saveToMetafield();
  }, 3000);
  
  window.WishlistManager = WishlistManager;
  window.wishlistManager = wishlistManager;
}
}

